import { useEffect } from 'react'
import { Blocker, useBlocker } from 'react-router-dom'
import { useAudioStore } from '../store/audioStore'

/**
 * Asks for confirmation before a page unload or an in-app route change while a
 * recording is active, so no page transition can silently kill the conversation (MD-1312).
 * In-app navigation is confirmed via a dialog rendered by the caller from the returned
 * blocker, since useBlocker's own predicate must decide synchronously, with no dialog of its own.
 */
export const useRecordingNavigationGuard = (): Blocker => {
    const isRecording = useAudioStore((state) => !!state.mediaRecorder)

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => isRecording && currentLocation.pathname !== nextLocation.pathname
    )

    useEffect(() => {
        if (!isRecording) return

        // Native pull-to-refresh would reload mid-recording; iOS Safari ignores beforeunload, so block the gesture
        const previousHtmlOverscroll = document.documentElement.style.overscrollBehaviorY
        const previousBodyOverscroll = document.body.style.overscrollBehaviorY
        document.documentElement.style.overscrollBehaviorY = 'none'
        document.body.style.overscrollBehaviorY = 'none'

        const warnBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            // Older Chromium versions only show the dialog when returnValue is set
            event.returnValue = ''
        }

        window.addEventListener('beforeunload', warnBeforeUnload)
        return () => {
            document.documentElement.style.overscrollBehaviorY = previousHtmlOverscroll
            document.body.style.overscrollBehaviorY = previousBodyOverscroll
            window.removeEventListener('beforeunload', warnBeforeUnload)
        }
    }, [isRecording])

    return blocker
}
